import { useMutation } from "@apollo/client";
import dynamic from "next/dynamic";
import { createContext, ReactNode, useMemo, useState } from "react";

import { cache } from "@/apollo-client";
import { SEARCH_ITEM_FIELDS } from "@/graphql/fragments/searchItem";
import CREATE_COLLECTION from "@/graphql/mutations/createCollection";
import DELETE_COLLECTION from "@/graphql/mutations/deleteCollection";
import EDIT_COLLECTION from "@/graphql/mutations/editCollection";
import SAVE from "@/graphql/mutations/save";
import UNSAVE from "@/graphql/mutations/unsave";
import { Collection } from "@/graphql/types/graphql";
import useMe from "@/hooks/useMe";
import useSnackbar from "@/hooks/useSnackbar";
import useTracker from "@/hooks/useTracker";

export interface CollectionContextValue {
  showDrawer: boolean;
  setShowDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  itemState: any;
  setItemState: React.Dispatch<React.SetStateAction<any>>;
  saveItem: ({
    item,
    collection,
    onSuccess,
  }: {
    item?: any;
    collection?: Collection;
    onSuccess?: () => void;
  }) => void;
  unsaveItem: ({
    item,
    onSuccess,
  }: {
    item: any;
    onSuccess?: () => void;
  }) => void;
  collections: Collection[];
  createCollection: ({
    name,
    onSuccess,
  }: {
    name: string;
    onSuccess?: (collection: Collection) => void;
  }) => void;
  editCollection: ({
    name,
    id,
    onSuccess,
  }: {
    name: string;
    id: string;
    onSuccess?: () => void;
  }) => void;
  deleteCollection: ({
    id,
    onSuccess,
  }: {
    id: string;
    onSuccess?: () => void;
  }) => void;
}

interface CollectionProviderProps {
  children?: ReactNode;
}

// Lazy load the CollectionDrawer
const LazyCollectionDrawer = dynamic(
  () => import("@/components/ui/collection/drawer"),
  { ssr: false } // Only needed on client
);

export const CollectionContext = createContext<CollectionContextValue>({
  showDrawer: false,
  setShowDrawer: () => {},
  itemState: null,
  setItemState: () => {},
  saveItem: () => {},
  unsaveItem: () => {},
  collections: [],
  createCollection: () => {},
  editCollection: () => {},
  deleteCollection: () => {},
});

const CollectionProvider = ({ children }: CollectionProviderProps) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [itemState, setItemState] = useState<any>(null);
  const [save] = useMutation(SAVE);
  const [unsave] = useMutation(UNSAVE);
  const { showSnackbar } = useSnackbar();
  const { me, fetchMe } = useMe();
  const [createCollectionMutation] = useMutation(CREATE_COLLECTION);
  const [editCollectionMutation] = useMutation(EDIT_COLLECTION);
  const [deleteCollectionMutation] = useMutation(DELETE_COLLECTION);
  const { saveEvent, unsaveEvent, createCollectionEvent } = useTracker();

  const collections = useMemo(() => {
    return me?.privateInfo?.collections || [];
  }, [me]);

  const saveItem: CollectionContextValue["saveItem"] = async ({
    item: itemArg,
    collection,
    onSuccess,
  }) => {
    const item = itemArg || itemState;

    const { data } = await save({
      variables: {
        input: {
          collectionID: collection?.id,
          itemID: item.id,
        },
      },
    });

    const result = data?.save?.result;

    if (result === "SUCCESS") {
      cache.updateFragment(
        {
          id: `Item:${item.id}`,
          fragment: SEARCH_ITEM_FIELDS,
        },
        (data) => ({ ...data, saved: true })
      );

      if (onSuccess) {
        onSuccess();
      }

      saveEvent({
        brand: item.brand,
        product_id: item.id,
        original_price: item.originalPrice,
        price: item.price,
        product_name: item.name,
        retailer: item.retailer,
        seo_url: item.seoURL,
        collection_id: collection?.id || "",
        collection_name: collection?.name || "",
      });
    }

    setItemState(null);
  };

  const unsaveItem: CollectionContextValue["unsaveItem"] = async ({
    item,
    onSuccess,
  }) => {
    const { data } = await unsave({
      variables: {
        input: {
          itemID: item.id,
        },
      },
    });

    const result = data?.unsave?.result;

    if (result === "SUCCESS") {
      cache.updateFragment(
        {
          id: `Item:${item.id}`,
          fragment: SEARCH_ITEM_FIELDS,
        },
        (data) => ({ ...data, saved: false })
      );

      showSnackbar(`Item unsaved succesfully`);
      if (onSuccess) {
        onSuccess();
      }

      unsaveEvent({
        brand: item.brand,
        product_id: item.id,
        original_price: item.originalPrice,
        price: item.price,
        product_name: item.name,
        retailer: item.retailer,
        seo_url: item.seoURL,
      });
    }

    setItemState(null);
  };

  const createCollection: CollectionContextValue["createCollection"] = async ({
    name,
    onSuccess,
  }) => {
    const { data } = await createCollectionMutation({
      variables: {
        input: {
          name,
        },
      },
    });

    const result = data?.createCollection?.result;

    if (result === "SUCCESS") {
      if (onSuccess) {
        onSuccess(data.createCollection.collection);
      }

      createCollectionEvent({
        collection_name: name,
        collection_id: data?.createCollection?.collection?.id,
      });
    }
  };

  const editCollection: CollectionContextValue["editCollection"] = async ({
    id,
    name,
    onSuccess,
  }) => {
    const { data } = await editCollectionMutation({
      variables: {
        input: {
          id,
          name,
        },
      },
    });

    const result = data?.editCollection?.result;

    if (result === "SUCCESS") {
      fetchMe();
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const deleteCollection: CollectionContextValue["deleteCollection"] = async ({
    id,
    onSuccess,
  }) => {
    const { data } = await deleteCollectionMutation({
      variables: {
        input: {
          id,
        },
      },
    });

    const result = data?.deleteCollection?.result;

    if (result === "SUCCESS") {
      fetchMe();
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const value = {
    showDrawer,
    setShowDrawer,
    itemState,
    setItemState,
    saveItem,
    unsaveItem,
    collections,
    createCollection,
    editCollection,
    deleteCollection,
  };

  return (
    <CollectionContext.Provider value={value}>
      {children}
      <LazyCollectionDrawer />
    </CollectionContext.Provider>
  );
};

export default CollectionProvider;
